import React, {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {FiArrowLeft,FiArrowRight,FiBarChart2,FiCheckCircle,FiChevronLeft,FiChevronRight,FiEye,FiFileText,FiGrid,FiRepeat,FiTarget,FiX,FiExternalLink} from 'react-icons/fi';

// Public, marketing-only image catalogue. Do not import AppContext, PocketBase,
// OAuth, Google Sheets, customer financial records, or any authenticated data.
const SHOTS=[
  {
    id:'dashboard',
    label:'Dashboard',
    title:'Understand your finances at a glance',
    description:'See financial summaries, trends, spending categories and useful insights in one organised dashboard.',
    image:'https://cloud.sharemyimage.com/2026/11/04/Dashboard.png',
    icon:FiBarChart2
  },
  {
    id:'transactions',
    label:'Transactions',
    title:'Find the transaction you need',
    description:'Review income and expenses in an organised table, with search, sorting and filters.',
    image:'https://cloud.sharemyimage.com/2026/11/04/Transactions2.png',
    icon:FiGrid
  },
  {
    id:'budgets',
    label:'Budgets',
    title:'Stay on top of your budget',
    description:'Compare spending with your category limits, view progress and spot budgets that need attention.',
    image:'https://cloud.sharemyimage.com/2026/11/04/Budgets.png',
    icon:FiTarget
  },
  {
    id:'recurring',
    label:'Recurring Transactions',
    title:'Plan repeated income and expenses',
    description:'Keep recurring rules together, review upcoming amounts and manage repeat transactions.',
    image:'https://cloud.sharemyimage.com/2026/11/04/Recurring-Transactions.png',
    icon:FiRepeat
  },
  {
    id:'reports',
    label:'Reports',
    title:'Turn financial records into reports',
    description:'Explore reporting views and export your financial information as a polished PDF or CSV.',
    image:'https://cloud.sharemyimage.com/2026/11/04/Reports.png',
    icon:FiFileText
  }
];

function Screenshot({shot,loading='lazy',className='',allowSourceLink=false}) {
  const [failed,setFailed]=useState(false);
  return <div className={'relative min-w-0 overflow-hidden bg-[#f3f7fc] '+className}>
    {!failed
      ?<img src={shot.image} alt={'Expense Manager Pro '+shot.label+' screenshot'}
        loading={loading} decoding="async" referrerPolicy="no-referrer"
        onError={()=>setFailed(true)}
        className="block h-full w-full object-contain object-top"/>
      :<div className="flex h-full min-h-[150px] flex-col items-center justify-center gap-2 p-5 text-center text-[#546b86]" role="status">
        <shot.icon aria-hidden="true" size={27}/>
        <span className="text-sm font-semibold">{shot.label} screenshot unavailable</span>
        <span className="text-xs">The external image host may be temporarily inaccessible.</span>
        {allowSourceLink&&<a href={shot.image} target="_blank" rel="noreferrer noopener"
          className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-[#0176D3] underline underline-offset-2">
          Open original image <FiExternalLink aria-hidden="true"/>
        </a>}
      </div>}
  </div>;
}

function ShowcaseContent({isModal=false,onClose}) {
  const [selected,setSelected]=useState(0);
  const active=SHOTS[selected];
  const touchX=useRef(null);
  const detailRef=useRef(null);

  const select=index=>{
    setSelected((index+SHOTS.length)%SHOTS.length);
  };
  const move=step=>select(selected+step);
  const seeShot=index=>{
    select(index);
    detailRef.current?.scrollIntoView?.({behavior:'smooth',block:'nearest'});
  };
  const handleKey=e=>{
    if(e.key==='ArrowRight'){e.preventDefault();move(1);}
    if(e.key==='ArrowLeft'){e.preventDefault();move(-1);}
  };
  return <div onKeyDown={handleKey} className="text-[#032D60]">
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="flex min-w-0 items-start gap-3">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-[#eaf4ff] text-[#0176D3]">
          <FiEye aria-hidden="true" size={22}/>
        </span>
        <div>
          <p className="text-[11px] font-extrabold uppercase tracking-[.16em] text-[#0176D3]">Product tour · 5 real screenshots</p>
          <h1 className="mt-1 text-xl font-extrabold tracking-tight sm:text-2xl">See Inside Expense Manager Pro</h1>
          <p className="mt-1 max-w-2xl text-xs leading-relaxed text-[#5a708c] sm:text-sm">
            Explore the key screens before signing in. The financial values in your own workspace will differ.
          </p>
        </div>
      </div>
      {isModal&&<button type="button" onClick={onClose} aria-label="Close app preview"
        className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-[#dce7f4] bg-white text-[#3c5574] transition hover:bg-[#eaf4ff] focus-visible:outline-2 focus-visible:outline-[#0176D3]">
        <FiX size={20}/>
      </button>}
    </div>

    <div className="mt-5 grid min-w-0 grid-cols-2 gap-3 lg:grid-cols-6" aria-label="App preview screenshot gallery">
      {SHOTS.map((shot,index)=>{
        const Icon=shot.icon;
        return <button type="button" key={shot.id} onClick={()=>seeShot(index)}
          aria-pressed={selected===index} aria-label={'Preview '+shot.label}
          className={'group min-w-0 overflow-hidden rounded-2xl border bg-white text-left shadow-sm outline-none transition hover:-translate-y-0.5 hover:shadow-md focus-visible:ring-2 focus-visible:ring-[#0176D3] '+
            (index===0?'col-span-2 lg:col-span-2 ':'')+
            (selected===index?'border-[#0176D3] ring-2 ring-[#0176D3]/15':'border-[#dce7f4] hover:border-[#91c8f4]')}>
          <Screenshot shot={shot} loading={index===0?'eager':'lazy'}
            className={'w-full border-b border-[#e6edf6] '+(index===0?'h-[148px] sm:h-[166px]':'h-[110px] sm:h-[125px]')}/>
          <div className="flex min-w-0 items-center gap-2 px-3 py-3">
            <span className={'grid h-7 w-7 shrink-0 place-items-center rounded-lg '+(selected===index?'bg-blue-100 text-[#0176D3]':'bg-slate-100 text-[#4e6580]')}>
              <Icon aria-hidden="true" size={15}/>
            </span>
            <span className="min-w-0 flex-1 truncate text-xs font-bold sm:text-sm">{shot.label}</span>
            <FiChevronRight aria-hidden="true" className="shrink-0 text-[#8195ad]"/>
          </div>
        </button>;
      })}
    </div>

    <section ref={detailRef} aria-label="Selected app preview" className="mt-5 min-w-0 overflow-hidden rounded-2xl border border-[#dce7f4] bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#edf2f8] px-4 py-3 sm:px-5">
        <div className="flex min-w-0 items-center gap-2">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#eaf4ff] text-[#0176D3]">
            <active.icon aria-hidden="true" size={18}/>
          </span>
          <div className="min-w-0">
            <h2 className="text-sm font-extrabold">{active.label}</h2>
            <p className="text-xs text-[#657c99]">{selected+1} of {SHOTS.length}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" aria-label="Previous screenshot" onClick={()=>move(-1)}
            className="grid h-10 w-10 place-items-center rounded-xl border border-[#dce7f4] text-[#0176D3] hover:bg-[#edf7ff] focus-visible:outline-2">
            <FiChevronLeft aria-hidden="true" size={19}/>
          </button>
          <button type="button" aria-label="Next screenshot" onClick={()=>move(1)}
            className="grid h-10 w-10 place-items-center rounded-xl border border-[#dce7f4] text-[#0176D3] hover:bg-[#edf7ff] focus-visible:outline-2">
            <FiChevronRight aria-hidden="true" size={19}/>
          </button>
        </div>
      </div>
      <div className="relative min-w-0 overflow-hidden bg-[#eef4fb] p-2 sm:p-4"
        onTouchStart={e=>{touchX.current=e.touches[0]?.clientX??null}}
        onTouchEnd={e=>{
          if(touchX.current==null)return;
          const difference=e.changedTouches[0]?.clientX-touchX.current;
          touchX.current=null;
          if(typeof difference==='number'&&Math.abs(difference)>55)move(difference<0?1:-1);
        }}>
        <Screenshot key={active.id} shot={active} loading="eager" allowSourceLink
          className="mx-auto h-[210px] w-full rounded-xl border border-[#d6e2ee] shadow-[0_7px_30px_rgba(3,45,96,.08)] sm:h-[340px] lg:h-[430px]"/>
      </div>
      <div className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
        <div className="min-w-0">
          <h3 className="text-sm font-extrabold">{active.title}</h3>
          <p className="mt-1 max-w-2xl text-xs leading-relaxed text-[#627991] sm:text-sm">{active.description}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5" aria-label="Select screenshot">
          {SHOTS.map((shot,index)=><button type="button" key={shot.id}
            onClick={()=>select(index)} aria-label={'Show '+shot.label}
            aria-current={selected===index?'true':undefined}
            className={'h-2.5 rounded-full transition-all focus-visible:outline-2 focus-visible:outline-[#0176D3] '+(selected===index?'w-6 bg-[#0176D3]':'w-2.5 bg-[#cad9eb] hover:bg-[#90b9e6]')}/>)}
        </div>
      </div>
    </section>
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-blue-100 bg-gradient-to-r from-[#edf7ff] to-white px-4 py-4 sm:px-5">
      <div className="flex min-w-0 items-start gap-2">
        <FiCheckCircle aria-hidden="true" className="mt-0.5 shrink-0 text-[#0176D3]"/>
        <div>
          <p className="text-sm font-bold">Ready for a more organised financial workspace?</p>
          <p className="mt-0.5 text-xs text-[#5c7290]">These are screenshots only. Your financial information remains private and requires secure sign-in.</p>
        </div>
      </div>
      {isModal
        ?<button type="button" onClick={onClose}
          className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-[#0176D3] px-4 py-2.5 text-xs font-bold text-white transition hover:bg-[#0064b8]">
          <FiArrowLeft aria-hidden="true"/> Return to sign in
        </button>
        :<a href="/" className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-[#0176D3] px-4 py-2.5 text-xs font-bold text-white transition hover:bg-[#0064b8]">
          Go to sign in <FiArrowRight aria-hidden="true"/>
        </a>}
    </div>
  </div>;
}

export function ExpenseShowcaseModal({onClose}) {
  const closeRef=useRef(null);
  useEffect(()=>{
    const previous=document.body.style.overflow;
    const before=document.activeElement;
    document.body.style.overflow='hidden';
    closeRef.current?.focus();
    const onKeyDown=event=>{
      if(event.key==='Escape'){event.preventDefault();onClose();}
      if(event.key==='Tab'){
        const focusables=closeRef.current?.parentElement?.querySelectorAll(
          'a[href],button:not([disabled]),input:not([disabled]),[tabindex]:not([tabindex="-1"])'
        )||[];
        const first=focusables[0],last=focusables[focusables.length-1];
        if(event.shiftKey&&document.activeElement===first){event.preventDefault();last?.focus();}
        else if(!event.shiftKey&&document.activeElement===last){event.preventDefault();first?.focus();}
      }
    };
    document.addEventListener('keydown',onKeyDown);
    return ()=>{
      document.body.style.overflow=previous;
      document.removeEventListener('keydown',onKeyDown);
      if(before?.isConnected)before.focus?.();
    };
  },[onClose]);
  return createPortal(
    <div className="fixed inset-0 z-[110] flex items-start justify-center overflow-y-auto bg-[#032D60]/70 p-2 backdrop-blur-[3px] sm:p-5"
      onMouseDown={e=>{if(e.target===e.currentTarget)onClose()}}>
      <section role="dialog" aria-modal="true" aria-label="See Inside Expense Manager Pro"
        className="my-auto w-full max-w-6xl overflow-hidden rounded-2xl border border-white/40 bg-[#f6f9fd] shadow-2xl sm:rounded-3xl">
        <div className="max-h-[calc(100dvh-16px)] overflow-y-auto px-4 py-5 sm:max-h-[calc(100dvh-40px)] sm:px-7 sm:py-7">
          <button type="button" ref={closeRef} onClick={onClose} aria-label="Close app preview" className="sr-only focus:not-sr-only focus:mb-3 focus:rounded-lg focus:bg-white focus:px-3 focus:py-2">Close preview</button>
          <ShowcaseContent isModal onClose={onClose}/>
        </div>
      </section>
    </div>,
    document.body
  );
}

export function ExpenseShowcasePage() {
  return <main className="relative min-h-[100dvh] bg-[#f1f6fc] px-3 py-5 font-sans sm:px-6 sm:py-8">
    <div className="absolute inset-0 -z-0 pointer-events-none" aria-hidden="true"
      style={{backgroundImage:'radial-gradient(#c9daef 0.8px,transparent 0.8px)',backgroundSize:'24px 24px',opacity:.3}}/>
    <div className="relative mx-auto max-w-6xl">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-xl bg-[#0176D3] font-extrabold text-white"><FiGrid aria-hidden="true" size={22}/></span>
          <div><p className="text-sm font-extrabold text-[#032D60]">Expense Manager Pro <span className="text-[#0176D3]">2.0</span></p>
            <p className="text-[11px] font-medium text-[#56708b]">BPA Solutions · Application preview</p></div>
        </div>
        <a href="/" className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-[#c7dbee] bg-white px-3 py-2 text-xs font-bold text-[#0176D3] hover:bg-blue-50">
          <FiArrowLeft aria-hidden="true"/> Sign in
        </a>
      </div>
      <div className="rounded-3xl border border-[#dbe7f4] bg-white p-4 shadow-[0_20px_70px_rgba(3,45,96,.08)] sm:p-7">
        <ShowcaseContent/>
      </div>
      <p className="py-5 text-center text-[11px] text-[#8294a9]">Preview screenshots supplied by BPA Solutions. Access to the live application requires an authorised account.</p>
    </div>
  </main>;
}